import { type ApiClient, createClient } from '@elite-residence/api-client';
import { createContext, type ReactNode, useContext } from 'react';

// The generated client is the only HTTP path to the API. Tests inject a
// client with a fake fetch; the browser uses same-origin requests.
const ApiClientContext = createContext<ApiClient | null>(null);

// CSRF double submit (X-09): the API sets a page-readable CSRF cookie with
// the session; every request echoes it in x-csrf-token. The session cookie
// itself is HttpOnly and never visible to scripts.
const CSRF_COOKIES = ['__Host-er_csrf', 'er_csrf'];

export function csrfToken(cookie: string = typeof document === 'undefined' ? '' : document.cookie): string | undefined {
  for (const part of cookie.split(';')) {
    const index = part.indexOf('=');
    if (index < 0) continue;
    if (CSRF_COOKIES.includes(part.slice(0, index).trim())) return part.slice(index + 1).trim();
  }
  return undefined;
}

export function browserApiClient(): ApiClient {
  return createClient({
    baseUrl: '',
    headers: () => {
      const token = csrfToken();
      return token ? { 'x-csrf-token': token } : {};
    },
  });
}

export function ApiClientProvider({ client, children }: { readonly client: ApiClient; readonly children: ReactNode }) {
  return <ApiClientContext.Provider value={client}>{children}</ApiClientContext.Provider>;
}

export function useApiClient(): ApiClient {
  const client = useContext(ApiClientContext);
  if (!client) throw new Error('useApiClient must be used inside ApiClientProvider');
  return client;
}
